import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Utilitários para formatação de datas com timezone correto
 */

/**
 * Formata uma string de data no formato YYYY-MM-DD para DD/MM/YYYY sem usar timezone.
 * Evita o problema de -1 dia quando a data é apenas dia (ex.: vencimento de boleto).
 */
export const formatDateOnlyBR = (dateStr: string | null | undefined): string => {
  if (!dateStr || typeof dateStr !== 'string') return '–';
  const trimmed = dateStr.trim();
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return formatDateBR(trimmed);
  return `${match[3]}/${match[2]}/${match[1]}`;
};

/**
 * Formata uma data para o timezone do Brasil (America/Sao_Paulo)
 * @param date - Data em string ou objeto Date
 * @param formatString - String de formato (padrão: 'dd/MM/yyyy')
 * @returns Data formatada no timezone do Brasil
 */
export const formatDateBR = (date: string | Date | null | undefined, formatString: string = 'dd/MM/yyyy'): string => {
  try {
    // Verificar se a data é nula ou indefinida
    if (!date) {
      return 'Data não disponível';
    }
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      console.warn('Data inválida recebida:', date);
      return 'Data inválida';
    }
    
    // Usar toLocaleDateString com timezone do Brasil
    return dateObj.toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      ...(formatString === 'dd/MM/yyyy' ? {} : {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    });
  } catch (error) {
    console.error('Erro ao formatar data:', error, 'Data original:', date);
    return 'Data inválida';
  }
};

/**
 * Formata uma data para exibição com horário no timezone do Brasil
 * @param date - Data em string ou objeto Date
 * @returns Data formatada com horário no timezone do Brasil
 */
export const formatDateTimeBR = (date: string | Date | null | undefined): string => {
  try {
    // Verificar se a data é nula ou indefinida
    if (!date) {
      return 'Data não disponível';
    }
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      console.warn('Data inválida recebida:', date);
      return 'Data inválida';
    }
    
    return dateObj.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (error) {
    console.error('Erro ao formatar data e hora:', error, 'Data original:', date);
    return 'Data inválida';
  }
};

/**
 * Formata um carimbo de entrega (ISO 8601) para 'dd/MM/yyyy HH:mm' no fuso
 * America/Sao_Paulo. Retorna '—' quando vazio/nulo ou data inválida.
 * Distinto de formatDateTimeBR (que inclui segundos e usa outro fallback).
 */
export const formatCarimboBR = (date: string | null | undefined): string => {
  if (!date) return '—';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '—';
  try {
    return dateObj.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

/**
 * Obtém a data atual no timezone do Brasil
 * @returns Data atual formatada no timezone do Brasil (DD/MM/YYYY)
 */
export const getCurrentDateBR = (): string => {
  const now = new Date();
  return now.toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Obtém a data atual no timezone do Brasil como objeto Date
 * @returns Data atual no timezone do Brasil
 */
export const getCurrentDateBRAsDate = (): Date => {
  const now = new Date();
  const brTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  return brTime;
};

/**
 * Converte uma data para o timezone do Brasil
 * @param date - Data em string ou objeto Date
 * @returns Data convertida para o timezone do Brasil
 */
export const convertToBRTimezone = (date: string | Date): Date => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return new Date();
    }
    
    // Converter para o timezone do Brasil
    const brTime = new Date(dateObj.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
    return brTime;
  } catch (error) {
    console.error('Erro ao converter timezone:', error);
    return new Date();
  }
};

/**
 * Formata uma data para exibição relativa (ex: "há 2 dias")
 * @param date - Data em string ou objeto Date
 * @returns Data formatada de forma relativa
 */
export const formatRelativeDateBR = (date: string | Date): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return 'Data inválida';
    }
    
    const now = new Date();
    const diffInMs = now.getTime() - dateObj.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return 'Hoje';
    } else if (diffInDays === 1) {
      return 'Ontem';
    } else if (diffInDays < 7) {
      return `Há ${diffInDays} dias`;
    } else {
      return formatDateBR(dateObj);
    }
  } catch (error) {
    console.error('Erro ao formatar data relativa:', error);
    return 'Data inválida';
  }
};

/**
 * Converte uma data do formato brasileiro (dd/MM/yyyy) para o formato ISO (yyyy-MM-dd)
 * @param dateBR - Data no formato brasileiro (dd/MM/yyyy)
 * @returns Data no formato ISO (yyyy-MM-dd) ou a própria string se já estiver em formato ISO
 */
export const convertBRDateToISO = (dateBR: string): string => {
  try {
    // Se já estiver em formato ISO (yyyy-MM-dd), retornar como está
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateBR)) {
      return dateBR;
    }
    
    // Se estiver em formato brasileiro (dd/MM/yyyy), converter
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateBR)) {
      const [day, month, year] = dateBR.split('/');
      return `${year}-${month}-${day}`;
    }
    
    console.warn('Formato de data não reconhecido:', dateBR);
    return dateBR;
  } catch (error) {
    console.error('Erro ao converter data BR para ISO:', error);
    return dateBR;
  }
};